import {Component, HostListener, Inject} from '@angular/core';
import {InsightPopupService} from '../insight-popup.service';
import {
    LEARNING_PROGRESS_INSIGHT,
    LearningProgressInsightService
} from '../../../../services/LearningProgressInsightService';
import {TimePeriodMode} from '../../../../services/rest/api-types/TimePeriodMode';
import {firstValueFrom, combineLatest, distinctUntilChanged} from 'rxjs';
import {filter, switchMap} from 'rxjs/operators';
import {TAB_NAVIGATION_SERVICE, TabNavigationService} from '../../../../services/TabNavigationService';
import {differenceInSeconds, intervalToDuration, isBefore} from 'date-fns';
import {
    AuthUntilUserRoleService
} from '../../../../../../../micromate-learn-web/src/app/core/authentication/auth-until-user-role.service';

@Component({
    selector: 'mm-learning-progress-history-insight-popup',
    templateUrl: './learning-progress-history-insight-popup.component.html',
    styleUrl: './learning-progress-history-insight-popup.component.scss',
    standalone: false
})
export class LearningProgressHistoryInsightPopupComponent {
    public showAdminData = false;

    constructor(public insightPopupService: InsightPopupService,
                @Inject(LEARNING_PROGRESS_INSIGHT) private learningProgressInsightService: LearningProgressInsightService,
                @Inject(TAB_NAVIGATION_SERVICE) public tabNavigationService: TabNavigationService,
                public roleService: AuthUntilUserRoleService) {
    }

    public timePeriodMode$ = this.tabNavigationService.timePeriodMode$.pipe(distinctUntilChanged());

    public learningPackageHistoryData$ = combineLatest([this.insightPopupService.selectedLearningPackageId$, this.timePeriodMode$]).pipe(
        filter((data): data is [string, TimePeriodMode] => data[0] !== undefined && data[1] !== undefined),
        switchMap(async ([learningPackageId, timePeriodMode]: [string, TimePeriodMode]) => {
            const learningProgressInsightHistory = await firstValueFrom(this.learningProgressInsightService.getLearningProgressInsightHistory(learningPackageId, timePeriodMode));
            return {data: learningProgressInsightHistory, timePeriodMode: timePeriodMode};
        })
    );

    @HostListener('document:keydown.i')
    public openAdminData(): void {
        this.showAdminData = !this.showAdminData;
    }

    public async setTimePeriod(mode: TimePeriodMode): Promise<void> {
        const selectedLearningPackageId = (await firstValueFrom(this.insightPopupService.selectedLearningPackageId$))!;
        void this.tabNavigationService.setTimePeriodModeForInsightPopupLearningHistoryTab(selectedLearningPackageId, mode);
    }

    protected readonly TimePeriodMode = TimePeriodMode;

    public timeFromNowToDate(otherDateRaw: string | undefined): string {
        if (otherDateRaw === undefined) {
            return '-';
        }

        const otherDate = new Date(otherDateRaw);
        const seconds = differenceInSeconds(new Date(), otherDate);
        const duration = intervalToDuration({start: 0, end: seconds * 1000});
        return `${isBefore(new Date(), otherDate) ? '' : '-'}${duration.months}m ${duration.days}d ${duration.hours}h`;
    }

    public daysToTime(days: number | undefined): string {
        if (days === undefined) {
            return '-';
        }
        const seconds = days * 24 * 60 * 60;
        const duration = intervalToDuration({start: 0, end: seconds * 1000});
        return `${duration.months ?? 0}m ${duration.days ?? 0}d ${duration.hours ?? 0}h`;
    }

    public formatStabilityOfMemory(stabilityOfMemory: number | undefined): string {
        return stabilityOfMemory === undefined ? '-' : stabilityOfMemory.toFixed(4);
    }

    public formatForgettingFactor(factor: number | undefined): string {
        return factor === undefined ? '-' : factor.toFixed(4);
    }
}
